<template>
  <div
    :class="[{ 'border border-orange-400 !min-h-[100px]': pendingCreation }]"
    class="relative flex flex-col justify-between min-h-[220px] px-4 py-2 bg-white dark:bg-dark-box-background shadow w-full whitespace-nowrap overflow-hidden rounded-lg hover:cursor-pointer"
    @click.stop="
      () => {
        pendingCreation
          ? $router.push(`/playbooks/new?name=${playbook.name}&is_public=${playbook.name}&description=${playbook.description}`)
          : $router.push(`/playbooks/${playbook?.id}`)
      }
    ">
    <div class="flex flex-col justify-between border-b">
      <div class="flex items-center justify-between font-bold">
        <div class="flex items-center truncate">
          <div
            class="text-lg font-bold leading-7 text-[#46545C] sm:leading-9 truncate dark:text-white hover:cursor-pointer"
            @click.stop="
              () => {
                pendingCreation
                  ? $router.push(`/playbooks/new?name=${playbook.name}&is_public=${playbook.name}&description=${playbook.description}`)
                  : $router.push(`/playbooks/${playbook?.id}`)
              }
            ">
            {{ playbook?.name }}
          </div>
          <div v-if="playbook?.description" class="cursor-pointer has-tooltip">
            <Info class="w-5 h-5 mr-2 text-common-grey" aria-hidden="true" />
            <span
              class="tooltip flex flex-wrap whitespace-pre-wrap px-2 py-2 rounded-md text-sm bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">{{ playbook?.description }}</span>
          </div>
        </div>
        <div v-if="!pendingCreation" class="flex items-start text-sm text-gray-500 rounded-full whitespace-nowrap" @click.stop="">
          <input
            v-if="showCheckbox"
            type="checkbox"
            class="mr-3 form-checkbox w-4 relative z-10 rounded h-4 dark:bg-dark-screen-background"
            :checked="isSelected"
            @input="(e) => $emit('update:isSelected', e.target.checked)">
          <SimpleActionDropDown
            classes="absolute flex-shrink-0 top-4 right-2"
            dropdown-class="absolute z-50 mx-3 -ml-[200px] whitespace-nowrap origin-bottom-right dark:bg-dark-screen-background w-52 mt-1 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
            :icon="EllipsisVertical"
            item-class="cursor-pointer group flex items-center px-4 py-2 text-gray-700 dark:text-dark-body-text w-full"
            item-active-class="bg-gray-100 dark:bg-dark-screen-background text-gray-400"
            :icon-button="true"
            icon-button-class="inline-flex items-center justify-center w-5 h-5 text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue"
            :items="[
              {
                name: $t(isSelected ? 'Unselect' : 'Select'),
                icon: SquareDashed,
                class: 'cursor-pointer group flex items-center px-4 py-2 text-gray-700 dark:text-dark-body-text w-full',
                activeClass: 'bg-gray-100 dark:bg-dark-screen-background text-gray-400',
                action: () => {
                  $emit('update:isSelected', !isSelected)
                }
              },
              {
                name: $t('Edit'),
                icon: Pencil,
                class: 'cursor-pointer group flex items-center px-4 py-2 text-gray-700 dark:text-dark-body-text w-full',
                activeClass: 'bg-gray-100 dark:bg-dark-screen-background text-gray-400',
                action: () => {
                  $router.push(`/playbooks/${playbook?.id}`)
                }
              },
              {
                name: $t('Duplicate'),
                icon: Copy,
                class: 'cursor-pointer group flex items-center px-4 py-2 text-gray-700 dark:text-dark-body-text w-full',
                activeClass: 'bg-gray-100 dark:bg-dark-screen-background text-gray-400',
                action: () => {
                  $emit('duplicatePlaybook', playbook)
                }
              },
              {
                name: $t(playbook?.is_active ? $t('Archive Playbook') : $t('Activate Playbook')),
                icon: playbook?.is_active
                  ? { render: () => h('span', { class: 'bg-red-500 rounded-full w-4 h-4 ml-0.5 mr-2' }) }
                  : { render: () => h('span', { class: 'bg-green-400 rounded-full w-4 h-4 ml-0.5 mr-2' }) },
                class: 'cursor-pointer group flex items-center px-4 py-2 text-gray-700 dark:text-dark-body-text w-full',
                activeClass: 'bg-gray-100 dark:bg-dark-screen-background text-gray-400',
                action: () => {
                  $emit('archivePlaybook', playbook)
                }
              },
              {
                name: $t('Delete Playbook'),
                icon: Trash2,
                iconClass: 'text-red-500 w-5 h-5 mr-2',
                class: 'cursor-pointer group flex items-center px-4 py-2 text-gray-700 dark:text-dark-body-text w-full',
                activeClass: 'bg-gray-100 dark:bg-dark-screen-background text-gray-400',
                action: () => {
                  $emit('deletePlaybook', playbook)
                }
              }
            ]" />
        </div>
        <div v-else>
          <Trash2
            class="text-common-grey transition-all ease-in-out hover:scale-125 hover:text-red-500 w-5 h-5 mr-2"
            @click.stop="$store.dispatch('data/savePendingPlaybook', null)" />
        </div>
      </div>
      <div class="mb-2 h-6 truncate">
        <div
          v-for="tag in playbook?.tags"
          :key="tag"
          :style="{ backgroundColor: `${tags?.find((t) => t.id == tag)?.color}12` }"
          class="inline-block px-2 mr-3 rounded-md">
          <div
            v-if="tags?.find((t) => t.id == tag)?.name"
            class="w-[10px] h-[10px] rounded-full inline-block mt-0 mr-2 text-transparent"
            :style="{ backgroundColor: tags?.find((t) => t.id == tag)?.color }" />
          <span :style="{ color: tags?.find((t) => t.id == tag)?.color }" class="text-sm">{{ tags?.find((t) => t.id == tag)?.name }}</span>
        </div>
      </div>
    </div>

    <div v-if="!pendingCreation" class="flex justify-between mt-2">
      <div class="text-common-grey">
        {{ $t("Created by") }}
      </div>
      <span class="inline-block">
        <Avatar :name="$utils.getUserById(playbook?.created_by, 'full_name')" />
      </span>
    </div>
    <div v-if="!pendingCreation">
      <div v-for="[key, value] in Object.entries(playbook?.stats)" :key="key" class="flex justify-between mt-2">
        <div class="text-common-grey">
          {{ playbook_stats_name_mapper[key] }}
        </div>
        <div>{{ value }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from "@/components/shared/Avatar.vue"
import SimpleActionDropDown from "@/components/shared/dropdowns/SimpleActionDropDown.vue"
import { Trash2, EllipsisVertical, Pencil, Copy, Info, SquareDashed } from "lucide-vue-next"
import { h } from "vue"

export default {
  components: {
    SimpleActionDropDown,
    Avatar,
    Info,
    Trash2
  },
  props: {
    tags: null,
    pendingCreation: null,
    isSelected: {
      type: Boolean,
      default: false
    },
    showCheckbox: {
      type: Boolean,
      default: false
    },
    playbook: {
      type: Object,
      default: null
    }
  },
  emits: ["deletePlaybook", "archivePlaybook", "duplicatePlaybook", "update:isSelected"],
  data() {
    return {
      h,
      Trash2,
      Copy,
      Pencil,
      SquareDashed,
      EllipsisVertical,
      playbook_stats_name_mapper: {
        executions: this.$t("Total Executions"),
        running: this.$t("Total Running"),
        succeeded: this.$t("Total Succeeded"),
        failed: this.$t("Total Failed"),
        aborted: this.$t("Total Aborted")
      }
    }
  },
  created() {},
  methods: {}
}
</script>
